import React, { useState, useEffect } from "react";
import "assets/scss/layouts/base-layout.scss";
import UserCheckoutId from "services/user-checkout-id";
import GlobalContext from "services/global-context";
import useToggle from "helpers/toggle-state";
import LoadingBar from "components/loading-bar";
import FixableContent from "components/fixable-content";
import Cart from "components/cart";
import { trackAddToCart, trackPageView } from "helpers/analytics";
import fixSlickVerticalBug from "helpers/slick-fix";
import { fetchCart, addCartItem, addDiscount } from "data/cart";
import AccountPanel from "components/account-panel";
import { associateCheckout, getCustomer, logout } from "services/shopify-account";
import gaEvent from "helpers/ga-event";
import CountdownBanner from "components/countdown-banner";
import { Slice } from "gatsby";
import PrivateSale from "../components/private-sale";
import GorgiasWidget from "../components/gorgias-widget";
import TrustedShopsWidget from "../components/trusted-shops-widget";

const BaseLayout = ({ data, children, handle, pageSettings, pageData }) => {
  const [loadingVisible, setLoadingVisible] = useState(false);
  const [cart, setCart] = useState({});
  const [cartOpened, toggleCartOpened] = useToggle();
  const [checkoutId, setCheckoutId] = useState(null);
  const [currentPanel, setCurrentPanel] = useState({
    prev: null,
    now: null,
    message: null,
  });
  const [loadingCustomer, setLoadingCustomer] = useState(true);
  const [currentCustomer, setCurrentCustomer] = useState(null);
  const [loyaltyLion, setLoyaltyLion] = useState(null);
  const [passwordEntered, setPasswordEntered] = useState(false);
  const [isPrivateSaleUnlocked, setIsPrivateSaleUnlocked] = useState(false);
  const [loadingPrivateSale, setLoadingPrivateSale] = useState(true);
  const code = data?.Cart?.discountCode ?? "";
  const isPrivateSaleEnabled = data?.PrivateSale?.isPrivateSaleEnabled;
  const fullHeight = data?.PrivateSale?.isPrivateSaleEnabled;
  const privateSalePassword = data?.PrivateSale?.privateSalePassword;
  const isBrowser = typeof window !== "undefined";

  useEffect(() => {
    trackPageView({ pageData });
  }, []);

  useEffect(fixSlickVerticalBug);

  useEffect(() => {
    const fetchData = async () => {
      const checkoutId = await UserCheckoutId();
      setCheckoutId(checkoutId);
      const cart = await fetchCart(checkoutId);

      if (cart.items.length) {
        associateCheckout(checkoutId);
      }
    };

    fetchData();
  }, []);

  useEffect(() => {
    getCustomerProfile();
    syncBodyOverlay();
  }, []);

  useEffect(() => {
    if (isPrivateSaleEnabled) {

      const openStore = localStorage.getItem("privateSalePassword");
      setIsPrivateSaleUnlocked(openStore === privateSalePassword);
    }
    setLoadingPrivateSale(false);
  }, [passwordEntered]);

  const refreshCart = async () => {
    setLoadingVisible(true);
    let cart = await fetchCart(checkoutId);

    if (cart) {
      localStorage.setItem("cartTotalQty", cart.totalQuantity);
      setCart(cart);
    }
    setLoadingVisible(false);
  };

  const addToCart = async (variant, products) => {
    await addCartItem(variant.id, variant?.store?.price, data?.Cart);
    refreshCart();
    await addDiscount(code);
    gaEvent("PDP", "Click", "Added to cart");
    trackAddToCart({ products: [...products, variant] });
  };

  const getCustomerProfile = (callback) => {
    getCustomer().then((customer) => {
      if (customer !== null) {
        setCurrentCustomer(customer);
        callback && callback();

      }
      setLoadingCustomer(false);
    });
  };

  const logOut = () => {
    logout();
    setCurrentCustomer(undefined);
    setLoadingCustomer(true);
    setTimeout(() => setLoadingCustomer(false), 100);
    localStorage.setItem("isLoggedIn", "false");
  };

  const toggleBodyOverlay = (opened) => {
    const bodyClassList = document.body.classList;

    if (opened) {
      bodyClassList.add("rm-overlay");
      setTimeout(() => {
        bodyClassList.toggle("with-overlay");
        bodyClassList.remove("rm-overlay");
      }, 500);
    } else {
      bodyClassList.toggle("with-overlay");
    }
  };

  const syncBodyOverlay = () => {
    const bodyClassList = document.body.classList;
    const panelClosed = !cartOpened && !currentPanel.now;
    if (panelClosed && [...bodyClassList]?.includes("with-overlay")) {
      bodyClassList.remove("with-overlay");
      bodyClassList.remove("rm-overlay");
    }
  };

  const toggleCartEvent = () => {
    toggleBodyOverlay(cartOpened);
    toggleCartOpened();
  };

  const setHistoricPanel = (_panel, _message) => {
    setCurrentPanel((_prev) => ({
      prev: _panel ? _prev.now : null,
      now: _panel ? _panel : null,
      message: _message ? _message : null
    }));
  };

  const headerSettings = data?.Header;
  const announcementBar = data?.AnnouncementBar;

  const globalState = {
    cartOpened: cartOpened,
    toggleCart: toggleCartEvent,
    checkoutId: checkoutId,
    setCheckoutId: setCheckoutId,
    cart: cart,
    setCart: setCart,
    refreshCart: refreshCart,
    loadingVisible: loadingVisible,
    setLoadingVisible: setLoadingVisible,
    addToCart: addToCart,
    toggleBodyOverlay: toggleBodyOverlay,
    currentPanel,
    setCurrentPanel: setHistoricPanel,
    currentCustomer,
    loadingCustomer,
    getCustomerProfile,
    logOut,
    loyaltyLion,
    setLoyaltyLion,
    specialOffers: data?.specialOffers,
    upcomingProducts: data?.upcomingProducts,
    disableAccounts: data?.Features.accounts,
    handle,
    disableScrollableMenu: headerSettings?.disableScrollableMenu,
    enabledAnnouncmenetBar: announcementBar?.show,
    cartSettings: data?.Cart,
    fullHeight: fullHeight
  };

  if (loadingPrivateSale) {
    return null;
  }

  if (isBrowser && window.location.pathname === "/private-sale-sign-up" && isPrivateSaleEnabled) {
    return <GlobalContext.Provider value={globalState}>
      {children}
    </GlobalContext.Provider>;
  }

  if (isPrivateSaleEnabled && !isPrivateSaleUnlocked) return <PrivateSale privateSale={data?.PrivateSale} passwordEntered={passwordEntered} setPasswordEntered={setPasswordEntered} timer={data?.Countdown?.timer} />;

  return <GlobalContext.Provider value={globalState}>
    <LoadingBar />
    <FixableContent hideHeaderBar={pageSettings?.hideHeaderBar} >
      <Slice alias="header" hideHeaderBar={pageSettings?.hideHeaderBar} />
    </FixableContent>
    <Cart data={data?.CartOverride} settings={data?.Cart} productSettings={data?.PDPSettings} handle={handle} />
    <AccountPanel currentPanel={currentPanel} />
    {data?.GorgiasWidgetAllPages?.show && <GorgiasWidget data={data?.GorgiasWidgetAllPages} />}
    <TrustedShopsWidget />
    <CountdownBanner data={data?.Countdown} handle={handle} excludeBanner={pageSettings?.excludeBanner} />
    {children}
    {!pageSettings?.hideFooter &&
      <Slice alias="footer" hideFooterNewsletter={pageSettings?.hideFooterNewsletter} />
    }
  </GlobalContext.Provider>;
};

export default BaseLayout;